import React, { useState, useEffect } from 'react';
import { 
  BarChart3, Download, Link2, Settings, 
  CheckCircle2, AlertCircle, ExternalLink
} from 'lucide-react';
import { 
  getBIConfigurations, createBIConfiguration, 
  exportToBI 
} from '../services/attendanceApi';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../services/api';

function BIIntegration() {
  const { t } = useLanguage();
  
  const [configurations, setConfigurations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    bi_tool: 'power_bi',
    api_key: '',
    webhook_url: '',
    export_format: 'json',
    auto_export: false,
    export_frequency: 'daily'
  });
  const [exporting, setExporting] = useState(false);

  // ⭐ NOVO: Assinatura Digital (Clicksign)
  const [esignConfig, setEsignConfig] = useState(null);
  const [showEsignConfigForm, setShowEsignConfigForm] = useState(false);
  const [esignTokenForm, setEsignTokenForm] = useState({ api_token: '', environment: 'sandbox', notification_email: '', webhook_secret: '' });
  const [savingEsignConfig, setSavingEsignConfig] = useState(false);
  const [esignFile, setEsignFile] = useState(null);
  // ⭐ NOVO: lista de signatários (suporta enviar o mesmo documento pra várias pessoas assinarem)
  const [esignSigners, setEsignSigners] = useState([
    { key: 'signer-1', deliveryType: 'email', name: '', email: '', phone: '', documentation: '', birthday: '' }
  ]);
  const addEsignSigner = () => {
    setEsignSigners(prev => [...prev, {
      key: `signer-${Date.now()}`,
      deliveryType: 'email', name: '', email: '', phone: '', documentation: '', birthday: ''
    }]);
  };
  const removeEsignSigner = (key) => {
    setEsignSigners(prev => prev.filter(s => s.key !== key));
  };
  const updateEsignSigner = (key, field, value) => {
    setEsignSigners(prev => prev.map(s => s.key === key ? { ...s, [field]: value } : s));
  };
  const [sendingDoc, setSendingDoc] = useState(false);
  const [currentTenantId, setCurrentTenantId] = useState(null);

  useEffect(() => {
    loadConfigurations();
    loadEsignConfig();
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCurrentTenantId(payload.tenant_id);
      } catch (err) {
        console.error('Erro ao ler token:', err);
      }
    }
  }, []);

  const loadEsignConfig = async () => {
    try {
      const res = await api.get('/api/esignature/config');
      setEsignConfig(res.data);
      setEsignTokenForm(prev => ({
        ...prev,
        environment: res.data.environment || 'sandbox',
        notification_email: res.data.notification_email || ''
      }));
    } catch (err) {
      console.error('Erro ao carregar configuração da Clicksign:', err);
    }
  };

  const loadConfigurations = async () => {
    try {
      const res = await getBIConfigurations();
      setConfigurations(res.data);
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createBIConfiguration(formData);
      setShowForm(false);
      setFormData({
        bi_tool: 'power_bi',
        api_key: '',
        webhook_url: '',
        export_format: 'json',
        auto_export: false,
        export_frequency: 'daily'
      });
      loadConfigurations();
      alert(t('biIntegration.configCreated'));
    } catch (err) {
      alert(t('biIntegration.errorCreate') + ': ' + (err.response?.data?.error || err.message));
    }
  };

  // ⭐ NOVO: salva o Access Token da Clicksign
  const handleSaveEsignConfig = async (e) => {
    e.preventDefault();
    if (!esignConfig?.configured && !esignTokenForm.api_token.trim()) {
      alert('Digite o Access Token');
      return;
    }
    setSavingEsignConfig(true);
    try {
      await api.post('/api/esignature/config', esignTokenForm);
      alert('✅ Configuração da Clicksign salva com sucesso!');
      setShowEsignConfigForm(false);
      setEsignTokenForm(prev => ({ ...prev, api_token: '', webhook_secret: '' }));
      loadEsignConfig();
    } catch (err) {
      alert('Erro ao salvar: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingEsignConfig(false);
    }
  };

  // ⭐ NOVO: envia o documento pra assinatura via Clicksign
  const handleSendDocument = async (e) => {
    e.preventDefault();
    if (!esignFile) {
      alert('Selecione um arquivo PDF');
      return;
    }
    if (esignSigners.length === 0) {
      alert('Adicione pelo menos um signatário');
      return;
    }
    for (const s of esignSigners) {
      if (!s.name.trim()) {
        alert('Preencha o nome de todos os signatários');
        return;
      }
      if (s.deliveryType === 'whatsapp' && !s.phone.trim()) {
        alert(`Preencha o telefone (com DDD) de ${s.name || 'um signatário'} pro envio por WhatsApp`);
        return;
      }
      if (s.deliveryType === 'email' && !s.email.trim()) {
        alert(`Preencha o email de ${s.name || 'um signatário'} pro envio por Email`);
        return;
      }
    }
    setSendingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', esignFile);
      // NOVO: manda a lista inteira de signatarios como JSON dentro do multipart
      formData.append('signers', JSON.stringify(esignSigners.map(s => ({
        name: s.name,
        delivery_type: s.deliveryType,
        email: s.email || undefined,
        phone: s.phone || undefined,
        documentation: s.documentation || undefined,
        birthday: s.birthday || undefined
      }))));
      await api.post('/api/esignature/send', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert(`Documento enviado para assinatura! ${esignSigners.length > 1 ? `Os ${esignSigners.length} signatarios vao` : 'O signatario vai'} receber a solicitacao.`);
      setEsignFile(null);
      setEsignSigners([{ key: 'signer-1', deliveryType: 'email', name: '', email: '', phone: '', documentation: '', birthday: '' }]);
    } catch (err) {
      alert('Erro ao enviar documento: ' + (err.response?.data?.error || err.message));
    } finally {
      setSendingDoc(false);
    }
  };

  const handleExport = async (biTool) => {
    setExporting(true);
    try {
      await exportToBI(biTool);
      alert(`✅ ${t('biIntegration.dataExported')} ${biTool === 'power_bi' ? 'Power BI' : 'Google Data Studio'}!`);
    } catch (err) {
      alert(t('biIntegration.errorExport') + ': ' + (err.response?.data?.error || err.message));
    } finally {
      setExporting(false);
    }
  };

  const biTools = [
    {
      id: 'power_bi',
      name: 'Microsoft Power BI',
      icon: '📊',
      color: 'from-yellow-500 to-yellow-600',
      description: t('biIntegration.powerBiDescription'),
      docs: 'https://docs.microsoft.com/power-bi'
    },
    {
      id: 'data_studio',
      name: 'Google Data Studio',
      icon: '📈',
      color: 'from-blue-500 to-blue-600',
      description: t('biIntegration.dataStudioDescription'),
      docs: 'https://datastudio.google.com'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
            <BarChart3 size={28} className="text-blue-600 dark:text-blue-400" />
            {t('biIntegration.title')}
          </h1>
          <p className="text-gray-500 dark:text-slate-400">{t('biIntegration.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 flex items-center gap-2"
        >
          <Link2 size={16} />
          {t('biIntegration.newIntegration')}
        </button>
      </div>

      {/* Cards das Ferramentas */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {biTools.map(tool => {
          const config = configurations.find(c => c.bi_tool === tool.id);
          return (
            <div key={tool.id} className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
              <div className={`p-5 bg-gradient-to-r ${tool.color} text-white`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl">{tool.icon}</span>
                    <div>
                      <h3 className="text-lg font-bold">{tool.name}</h3>
                      <p className="text-xs opacity-90">{tool.description}</p>
                    </div>
                  </div>
                  {config && (
                    <CheckCircle2 size={24} className="text-white" />
                  )}
                </div>
              </div>
              <div className="p-5 space-y-3">
                {config ? (
                  <>
                    <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                      <CheckCircle2 size={16} />
                      <span className="font-medium">{t('biIntegration.activeConfig')}</span>
                    </div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">
                      <p><strong>{t('biIntegration.format')}:</strong> {config.export_format?.toUpperCase()}</p>
                      <p><strong>{t('biIntegration.autoExport')}:</strong> {config.auto_export ? t('common.yes') : t('common.no')}</p>
                      <p><strong>{t('biIntegration.frequency')}:</strong> {t(`biIntegration.freq_${config.export_frequency}`)}</p>
                      {config.last_export_at && (
                        <p><strong>{t('biIntegration.lastExport')}:</strong> {new Date(config.last_export_at).toLocaleString('pt-BR')}</p>
                      )}
                    </div>
                    <button
                      onClick={() => handleExport(tool.id)}
                      disabled={exporting}
                      className="w-full py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      <Download size={16} />
                      {exporting ? t('biIntegration.exporting') : t('biIntegration.exportNow')}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                      <AlertCircle size={16} />
                      <span>{t('biIntegration.notConfigured')}</span>
                    </div>
                    <button
                      onClick={() => {
                        setFormData({ ...formData, bi_tool: tool.id });
                        setShowForm(true);
                      }}
                      className="w-full py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-gray-200 dark:hover:bg-slate-600 flex items-center justify-center gap-2"
                    >
                      <Settings size={16} />
                      {t('biIntegration.configure')}
                    </button>
                  </>
                )}
                <a
                  href={tool.docs}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 flex items-center gap-1"
                >
                  <ExternalLink size={12} />
                  {t('biIntegration.viewDocs')}
                </a>
              </div>
            </div>
          );
        })}
      </div>

      {/* ⭐ NOVO: Assinatura Digital (Clicksign) */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
        <div className="p-5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white">
          <div className="flex items-center gap-3">
            <span className="text-4xl">✍️</span>
            <div>
              <h3 className="text-lg font-bold">Assinatura Digital — Clicksign</h3>
              <p className="text-xs opacity-90">Envie documentos para assinatura eletrônica</p>
            </div>
          </div>
        </div>
        <div className="p-5 space-y-4">
          {esignConfig?.configured ? (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-green-600 dark:text-green-400">
                  <CheckCircle2 size={16} />
                  <span className="font-medium">
                    Configurado ({esignConfig.environment === 'production' ? 'Produção' : 'Sandbox'})
                  </span>
                </div>
                <button
                  onClick={() => setShowEsignConfigForm(true)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Trocar Access Token
                </button>
              </div>

              <form onSubmit={handleSendDocument} className="space-y-3 pt-3 border-t border-gray-100 dark:border-slate-700">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Documento (PDF) *
                  </label>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={e => setEsignFile(e.target.files[0] || null)}
                    className="w-full text-sm text-gray-600 dark:text-slate-400"
                  />
                </div>

                {/* NOVO: lista de signatarios (pode adicionar mais de uma pessoa) */}
                {esignSigners.map((signer, idx) => (
                  <div key={signer.key} className="border border-gray-200 dark:border-slate-600 rounded-xl p-3 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold text-gray-500 dark:text-slate-400">
                        Signatario {idx + 1}
                      </span>
                      {esignSigners.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeEsignSigner(signer.key)}
                          className="text-xs text-red-600 dark:text-red-400 hover:underline"
                        >
                          Remover
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                          Envio *
                        </label>
                        <select
                          value={signer.deliveryType}
                          onChange={e => updateEsignSigner(signer.key, 'deliveryType', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                        >
                          <option value="email">E-mail</option>
                          <option value="whatsapp">WhatsApp</option>
                        </select>
                      </div>
                      <div>
                        {signer.deliveryType === 'whatsapp' ? (
                          <>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                              Telefone (com DDD) *
                            </label>
                            <input
                              type="text"
                              value={signer.phone}
                              onChange={e => updateEsignSigner(signer.key, 'phone', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                              placeholder="(11) 99999-9999"
                            />
                          </>
                        ) : (
                          <>
                            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                              Email *
                            </label>
                            <input
                              type="email"
                              value={signer.email}
                              onChange={e => updateEsignSigner(signer.key, 'email', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                              placeholder="joao@email.com"
                            />
                          </>
                        )}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                        Nome do Signatario *
                      </label>
                      <input
                        type="text"
                        value={signer.name}
                        onChange={e => updateEsignSigner(signer.key, 'name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                        placeholder="Ex: Joao Silva"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                          CPF (opcional)
                        </label>
                        <input
                          type="text"
                          value={signer.documentation}
                          onChange={e => updateEsignSigner(signer.key, 'documentation', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                          placeholder="000.000.000-00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                          Data de Nascimento (opcional)
                        </label>
                        <input
                          type="date"
                          value={signer.birthday}
                          onChange={e => updateEsignSigner(signer.key, 'birthday', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                        />
                      </div>
                    </div>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addEsignSigner}
                  className="w-full py-2 border border-dashed border-emerald-400 dark:border-emerald-600 text-emerald-700 dark:text-emerald-400 rounded-xl text-sm font-medium hover:bg-emerald-50 dark:hover:bg-emerald-900/20"
                >
                  + Adicionar pessoa
                </button>
                <button
                  type="submit"
                  disabled={sendingDoc}
                  className="w-full py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                >
                  {sendingDoc ? 'Enviando...' : 'Enviar para Assinatura'}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-slate-400">
                <AlertCircle size={16} />
                <span>Não configurado</span>
              </div>
              <button
                onClick={() => setShowEsignConfigForm(true)}
                className="w-full py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-xl text-sm font-medium hover:bg-gray-200 dark:hover:bg-slate-600 flex items-center justify-center gap-2"
              >
                <Settings size={16} />
                Configurar
              </button>
            </>
          )}
          <a href="https://developers.clicksign.com/" target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 flex items-center gap-1">
            <ExternalLink size={12} />
            Ver documentação da Clicksign
          </a>
        </div>
      </div>

      {/* ⭐ NOVO: modal de configuração do Access Token da Clicksign */}
      {showEsignConfigForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-5">
              <h2 className="text-xl font-bold">Configurar Clicksign</h2>
              <p className="text-sm opacity-90">Cole o Access Token gerado na sua conta Clicksign</p>
            </div>
            <form onSubmit={handleSaveEsignConfig} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Access Token
                </label>
                <input
                  type="password"
                  value={esignTokenForm.api_token}
                  onChange={e => setEsignTokenForm({ ...esignTokenForm, api_token: e.target.value })}
                  placeholder="Cole o token aqui"
                  className="w-full px-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Ambiente
                </label>
                <select
                  value={esignTokenForm.environment}
                  onChange={e => setEsignTokenForm({ ...esignTokenForm, environment: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                >
                  <option value="sandbox">Sandbox (testes, sem valor legal)</option>
                  <option value="production">Produção</option>
                </select>
              </div>

              {/* ⭐ NOVO: email de notificação */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Email de notificação (opcional)
                </label>
                <input
                  type="email"
                  value={esignTokenForm.notification_email}
                  onChange={e => setEsignTokenForm({ ...esignTokenForm, notification_email: e.target.value })}
                  placeholder="alguem@suaempresa.com"
                  className="w-full px-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                />
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  Pra onde enviamos o PDF assinado quando o documento é finalizado.
                </p>
              </div>

              {/* ⭐ NOVO: segredo do webhook */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Segredo do Webhook {esignConfig?.has_webhook_secret ? '(já configurado)' : '(opcional)'}
                </label>
                <input
                  type="password"
                  value={esignTokenForm.webhook_secret}
                  onChange={e => setEsignTokenForm({ ...esignTokenForm, webhook_secret: e.target.value })}
                  placeholder={esignConfig?.has_webhook_secret ? 'Deixe em branco pra manter o atual' : 'Crie uma senha aleatória'}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
                />
              </div>

              {/* ⭐ NOVO: URL do webhook pronta pra copiar e colar na Clicksign */}
              {esignTokenForm.webhook_secret && currentTenantId && (
                <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl p-3">
                  <label className="block text-xs font-medium text-emerald-800 dark:text-emerald-300 mb-1">
                    URL do Webhook (cole na configuração da Clicksign)
                  </label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-lg px-2 py-1.5 overflow-x-auto whitespace-nowrap">
                      {`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/esignature-webhook/${currentTenantId}?secret=${esignTokenForm.webhook_secret}`}
                    </code>
                    <button
                      type="button"
                      onClick={() => navigator.clipboard.writeText(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/esignature-webhook/${currentTenantId}?secret=${esignTokenForm.webhook_secret}`)}
                      className="shrink-0 text-xs font-medium text-emerald-700 dark:text-emerald-400 hover:underline"
                    >
                      copiar
                    </button>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowEsignConfigForm(false)}
                  className="flex-1 py-2 border border-gray-200 dark:border-slate-600 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={savingEsignConfig}
                  className="flex-1 py-2 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 disabled:opacity-50"
                >
                  {savingEsignConfig ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Formulário */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-5">
              <h2 className="text-xl font-bold">{t('biIntegration.newIntegration')}</h2>
              <p className="text-sm opacity-90">{t('biIntegration.configureConnection')}</p>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  {t('biIntegration.biTool')}
                </label>
                <select
                  value={formData.bi_tool}
                  onChange={e => setFormData({ ...formData, bi_tool: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="power_bi">Microsoft Power BI</option>
                  <option value="data_studio">Google Data Studio</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  {t('biIntegration.webhookUrl')}
                </label>
                <input
                  type="url"
                  value={formData.webhook_url}
                  onChange={e => setFormData({ ...formData, webhook_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full px-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  {t('biIntegration.apiKey')}
                </label>
                <input
                  type="text"
                  value={formData.api_key}
                  onChange={e => setFormData({ ...formData, api_key: e.target.value })}
                  placeholder={t('biIntegration.authKey')}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  {t('biIntegration.exportFormat')}
                </label>
                <select
                  value={formData.export_format}
                  onChange={e => setFormData({ ...formData, export_format: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="json">JSON</option>
                  <option value="csv">CSV</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  {t('biIntegration.exportFrequency')}
                </label>
                <select
                  value={formData.export_frequency}
                  onChange={e => setFormData({ ...formData, export_frequency: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                >
                  <option value="hourly">{t('biIntegration.freq_hourly')}</option>
                  <option value="daily">{t('biIntegration.freq_daily')}</option>
                  <option value="weekly">{t('biIntegration.freq_weekly')}</option>
                </select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="auto_export"
                  checked={formData.auto_export}
                  onChange={e => setFormData({ ...formData, auto_export: e.target.checked })}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <label htmlFor="auto_export" className="text-sm text-gray-700 dark:text-slate-300">
                  {t('biIntegration.autoExport')}
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="flex-1 py-2 border border-gray-200 dark:border-slate-600 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2 bg-blue-600 text-white rounded-xl text-sm font-bold hover:bg-blue-700"
                >
                  {t('common.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default BIIntegration;